import { getSelectedNode } from "../../utils/getSelectedNode"
import { blockTypeToBlockName } from "./constants"
import { $findMatchingParent, $getNearestBlockElementAncestorOrThrow, $getNearestNodeOfType } from '@lexical/utils'
import {
   $getNodeByKey,
   $getSelection,
   $isRangeSelection,
   $isRootOrShadowRoot,
   $isTextNode,
   ElementNode,
   FORMAT_ELEMENT_COMMAND,
   FORMAT_TEXT_COMMAND,
   INDENT_CONTENT_COMMAND,
   LexicalNode,
   OUTDENT_CONTENT_COMMAND,
   REDO_COMMAND,
   TextNode,
   UNDO_COMMAND,
} from 'lexical'
import { $getSelectionStyleValueForProperty, $isParentElementRTL, $patchStyleText, $selectAll } from '@lexical/selection'
import type { ElementFormatType, LexicalEditor, NodeKey, TextFormatType } from 'lexical'
import { $isCodeNode, CODE_LANGUAGE_MAP } from '@lexical/code'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { $isListNode, ListNode } from '@lexical/list'
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode'
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode'
import { $isHeadingNode } from '@lexical/rich-text'
import { sanitizeUrl } from "../../utils/url"
import { ToolbarShowOptions } from "../../LexicalTypes"
import { arrayToSimpleObject } from "reusable/utils/array-utils"
import ModalState from "reusable/mobx-helpers/ModalState"

interface ToolbarPluginStoreProps {
   orgId: string,
   editor: LexicalEditor
   show?: ToolbarShowOptions[]
}

export default class ToolbarPluginStore {
   sp
   blockType: keyof typeof blockTypeToBlockName = 'paragraph'
   fontSize = '15px'
   fontColor = '#000'
   bgColor = '#fff'
   fontFamily = 'DMSans'
   isLink = false
   isBold = false
   isItalic = false
   isUnderline = false
   isStrikethrough = false
   isSubscript = false
   isSuperscript = false
   isCode = false
   canUndo = false
   canRedo = false
   isRTL = false
   codeLanguage = ''
   selectedElementKey: NodeKey | null = null
   activeEditor: LexicalEditor
   insertImageModal = new ModalState()
   insertTableModal = new ModalState()
   insertExperimentalTabelModal = new ModalState()

   constructor(sp: ToolbarPluginStoreProps) {
      this.sp = sp
      this.activeEditor = sp.editor
   }

   get toolbarExtensions() {
      return arrayToSimpleObject<Partial<ToolbarShowOptions>>(this.sp.show || [])
   }

   updateToolbar = (editor: LexicalEditor = this.activeEditor) => {
      editor.getEditorState().read(() => {
         const selection = $getSelection()
         if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode()
            let element: ElementNode | TextNode | LexicalNode | null =
               anchorNode.getKey() === 'root'
                  ? anchorNode
                  : ($findMatchingParent(anchorNode, (e) => {
                     const parent = e.getParent()
                     return parent !== null && $isRootOrShadowRoot(parent)
                  }) as ElementNode | TextNode | null)

            if (element === null) {
               element = anchorNode.getTopLevelElementOrThrow()
            }

            const elementKey = element.getKey()
            const elementDOM = this.activeEditor.getElementByKey(elementKey)

            // Update text format
            this.isBold = selection.hasFormat('bold')
            this.isItalic = selection.hasFormat('italic')
            this.isUnderline = selection.hasFormat('underline')
            this.isStrikethrough = selection.hasFormat('strikethrough')
            this.isSubscript = selection.hasFormat('subscript')
            this.isSuperscript = selection.hasFormat('superscript')
            this.isCode = selection.hasFormat('code')
            this.isRTL = $isParentElementRTL(selection)

            // Update links
            const node = getSelectedNode(selection)
            const parent = node.getParent()
            if ($isLinkNode(parent) || $isLinkNode(node)) {
               this.isLink = true
            } else {
               this.isLink = false
            }

            if (elementDOM !== null) {
               this.selectedElementKey = elementKey
               if ($isListNode(element)) {
                  const parentList = $getNearestNodeOfType<ListNode>(
                     anchorNode,
                     ListNode,
                  )
                  const type = parentList
                     ? parentList.getListType()
                     : element.getListType()
                  this.blockType = type
               } else {
                  const type = $isHeadingNode(element)
                     ? element.getTag()
                     : element.getType()
                  if (type in blockTypeToBlockName) {
                     this.blockType = type as keyof typeof blockTypeToBlockName
                  }
                  if ($isCodeNode(element)) {
                     const language =
                        element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP
                     this.codeLanguage = language ? CODE_LANGUAGE_MAP[language] || language : ''
                     return
                  }
               }
            }
            // Handle buttons
            this.fontSize = $getSelectionStyleValueForProperty(selection, 'font-size', '15px')
            this.fontColor = $getSelectionStyleValueForProperty(selection, 'color', '#000')
            this.bgColor = $getSelectionStyleValueForProperty(selection, 'background-color', '#fff')
            this.fontFamily = $getSelectionStyleValueForProperty(selection, 'font-family', 'DMSans')
         }
      }, { editor })
   }

   applyStyleText = (styles: Record<string, string>) => {
      this.activeEditor.update(() => {
         const selection = $getSelection()
         if ($isRangeSelection(selection)) {
            $patchStyleText(selection, styles)
         }
      })
   }

   clearFormatting = () => {
      this.activeEditor.update(() => {
         const selection = $getSelection()
         if ($isRangeSelection(selection)) {
            $selectAll(selection)
            selection.getNodes().forEach((node) => {
               if ($isTextNode(node)) {
                  node.setFormat(0)
                  node.setStyle('')
                  const blockElement = $getNearestBlockElementAncestorOrThrow(node as TextNode)
                  blockElement.setFormat('')
               }
               if ($isDecoratorBlockNode(node)) {
                  node.setFormat('')
               }
            })
         }
      })
   }

   insertLink = () => {
      if (!this.isLink) {
         this.sp.editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'))
      } else {
         this.sp.editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
      }
   }

   onFontColorSelect = (value: string) => {
      return this.applyStyleText({ color: value })
   }

   onBgColorSelect = (value: string) => {
      return this.applyStyleText({ 'background-color': value })
   }

   onCodeLanguageSelect = (e: React.MouseEvent<HTMLElement>) => {
      const value = e.currentTarget.getAttribute("data-id")
      if (value) {
         this.sp.editor.update(() => {
            if (this.selectedElementKey !== null) {
               const node = $getNodeByKey(this.selectedElementKey)
               if (node && $isCodeNode(node)) {
                  node.setLanguage(value)
               }
            }
         })
      }
   }

   dispatchCommand = (e: React.MouseEvent<HTMLElement>) => {
      const command = e.currentTarget.getAttribute("data-id")
      const type = e.currentTarget.getAttribute("data-type") || ""

      switch (command) {
         case 'UNDO_COMMAND':
            this.activeEditor.dispatchCommand(UNDO_COMMAND, undefined)
            break
         case 'REDO_COMMAND':
            this.activeEditor.dispatchCommand(REDO_COMMAND, undefined)
            break
         case 'FORMAT_TEXT_COMMAND':
            this.activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, type as TextFormatType)
            break
         case 'INSERT_HORIZONTAL_RULE_COMMAND':
            this.activeEditor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)
            break
         case 'FORMAT_ELEMENT_COMMAND':
            this.activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, type as ElementFormatType)
            break
         case 'OUTDENT_CONTENT_COMMAND':
            this.activeEditor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
            break
         case 'INDENT_CONTENT_COMMAND':
            this.activeEditor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
            break
      }
   }
}
